<script setup lang="ts">
import { inject, onBeforeUnmount, watch, watchEffect } from 'vue';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';
import vtkMapper from '@kitware/vtk.js/Rendering/Core/Mapper';
import type vtkPolyData from '@kitware/vtk.js/Common/DataModel/PolyData';
import { VtkViewContext } from '@/src/components/vtk/context';
import { useImageCacheStore } from '@/src/store/image-cache';
import { useSegmentationStore } from '@/src/segmentation/store';
import { useMaskRevision } from '@/src/segmentation/composables/useMaskRevision';
import { createSurfaceBuilder } from './surfaceWorker';
import { useDentalCbctStore } from './cbctStore';
import { useDentalSceneStore } from './sceneStore';
import { observeActor } from './actorObservation';
import { useDentalAppearanceStore } from './appearanceStore';
const props = defineProps<{ imageId?: string | null }>();
const view = inject(VtkViewContext); if (!view) throw new Error('A vtk view is required.');
const cbct = useDentalCbctStore(); const scene = useDentalSceneStore();
const appearance = useDentalAppearanceStore();
const masks = useSegmentationStore(); const cache = useImageCacheStore();
const revision = useMaskRevision(() => props.imageId);
type Entry = { actor: vtkActor; mapper: vtkMapper; poly: vtkPolyData; mtime: number; source: object; remove: () => void };
const entries = new Map<string, Entry>();
const surfaceBuilder = createSurfaceBuilder();
let timer: ReturnType<typeof setTimeout> | undefined; let generation = 0; let disposed = false;
function remove(id: string) {
  const entry = entries.get(id); if (!entry) return;
  entry.remove(); view!.renderer.removeActor(entry.actor); entry.actor.delete(); entry.mapper.delete(); entry.poly.delete(); entries.delete(id);
}
function style() {
  for (const [id, entry] of entries) {
    const row = cbct.instances.find(instance => instance.segmentId === id);
    entry.actor.setVisibility(!!row && appearance.visible(row) && scene.segmentationVisible && (!scene.selectedOnly || cbct.selected?.segmentId === id) && entry.poly.getNumberOfPoints() > 0);
    if (row) entry.actor.getProperty().setColor(...appearance.color(row).slice(0,3).map(value => value / 255) as [number, number, number]);
    entry.actor.getProperty().setOpacity(row?.opacity ?? scene.segmentationOpacity);
  }
  view!.requestRender();
}
function schedule() {
  generation += 1; clearTimeout(timer);
  surfaceBuilder.cancel();
  if (!scene.segmentationVisible) { scene.surfaceBusy = false; return; }
  scene.surfaceBusy = true;
  scene.surfaceCompleted = 0; scene.surfaceTotal = cbct.instances.length;
  timer = setTimeout(() => void build(generation), 250);
}
async function build(token: number) {
  const parent = cache.getVtkImageData(props.imageId);
  if (!parent || disposed) return;
  scene.surfaceBusy = true; scene.surfaceError = ''; const start = performance.now();
  try {
    const live = new Set(cbct.instances.map(row => row.segmentId));
    for (const id of entries.keys()) if (!live.has(id)) remove(id);
    for (const row of cbct.instances) {
      if (token !== generation || disposed) return;
      const binding = masks.maskFor(props.imageId, row.segmentId)?.representations.labelmap;
      if (!binding) { remove(row.segmentId); continue; }
      const existing = entries.get(row.segmentId);
      if (!existing || existing.source !== binding.image || existing.mtime !== binding.image.getMTime()) {
        const mtime = binding.image.getMTime();
        const poly = await surfaceBuilder.build(parent, binding);
        if (token !== generation || disposed) { poly.delete(); return; }
        if (existing) {
          existing.mapper.setInputData(poly); existing.poly.delete(); existing.poly = poly;
          existing.mtime = mtime; existing.source = binding.image;
        } else {
          const mapper = vtkMapper.newInstance({ scalarVisibility: false }); mapper.setInputData(poly);
          const actor = vtkActor.newInstance(); actor.setMapper(mapper); actor.setPickable(false);
          actor.getProperty().setAmbient(0.65); actor.getProperty().setDiffuse(0.35);
          view!.renderer.addActor(actor);
          entries.set(row.segmentId, { actor, mapper, poly, mtime, source: binding.image,
            remove: observeActor(actor, view!.renderer, { kind: 'segmentation', labelValue: row.labelValue }) });
        }
        style();
        // Yield between bounded masks; never rescan every segment per frame.
        await new Promise<void>(resolve => setTimeout(resolve, 0));
      }
      scene.surfaceCompleted += 1;
    }
    scene.surfaceCount = [...entries.values()].filter(entry => entry.poly.getNumberOfPoints() > 0).length;
    scene.surfaceBuildMs = performance.now() - start;
    view!.renderer.resetCameraClippingRange(); style();
  } catch (error) { if ((error as Error).name !== 'AbortError') scene.surfaceError = (error as Error).message; }
  finally { if (token === generation) scene.surfaceBusy = false; }
}
watch([() => props.imageId, revision, () => cbct.instances.map(row => row.segmentId).join(','), () => scene.segmentationVisible], schedule, { immediate: true });
watch(() => [scene.segmentationVisible, scene.selectedOnly, scene.segmentationOpacity, cbct.selected?.segmentId,
  cbct.instances.map(row => [row.id, appearance.visible(row), row.opacity, ...appearance.color(row)])], style, { deep: true, immediate: true });
onBeforeUnmount(() => { disposed = true; generation += 1; clearTimeout(timer); surfaceBuilder.cancel(); for (const id of entries.keys()) remove(id); scene.surfaceBusy = false; scene.surfaceCount = 0; });
</script>
<template><slot /></template>
